import { useState } from "react";
import { BookOpen, ChevronDown, ChevronRight, FolderOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Curated technology keywords to logo images and premium brand colors
const TECH_INFO_MAP = [
  { keywords: ['ansible'], icon: 'https://cdn.simpleicons.org/ansible/EE0000', color: '#EE0000', name: 'Ansible' },
  { keywords: ['apache', 'httpd', 'web server'], icon: 'https://cdn.simpleicons.org/apache/D22128', color: '#D22128', name: 'Apache' },
  { keywords: ['tomcat'], icon: 'https://cdn.simpleicons.org/apachetomcat/F8DC75', color: '#F8DC75', name: 'Tomcat' },
  { keywords: ['docker'], icon: 'https://cdn.simpleicons.org/docker/2496ED', color: '#2496ED', name: 'Docker' },
  { keywords: ['kubernetes', 'k8s'], icon: 'https://cdn.simpleicons.org/kubernetes/326CE5', color: '#326CE5', name: 'Kubernetes' },
  { keywords: ['jenkins'], icon: 'https://cdn.simpleicons.org/jenkins/D24939', color: '#D24939', name: 'Jenkins' },
  { keywords: ['git', 'gitlab'], icon: 'https://cdn.simpleicons.org/gitlab/FC6D26', color: '#FC6D26', name: 'GitLab' },
  { keywords: ['linux'], icon: 'https://cdn.simpleicons.org/linux/FCC624', color: '#FCC624', name: 'Linux' },
  { keywords: ['postgres', 'postgresql'], icon: 'https://cdn.simpleicons.org/postgresql/4169E1', color: '#4169E1', name: 'PostgreSQL' },
  { keywords: ['zabbix'], icon: 'https://cdn.simpleicons.org/zabbix/D40000', color: '#D40000', name: 'Zabbix' },
  { keywords: ['openshift'], icon: 'https://cdn.simpleicons.org/redhatopenshift/EE0000', color: '#EE0000', name: 'OpenShift' },
  { keywords: ['devops', 'cicd', 'ci/cd'], icon: 'https://cdn.simpleicons.org/azuredevops/0078D7', color: '#0078D7', name: 'DevOps' },
  { keywords: ['python'], icon: 'https://cdn.simpleicons.org/python/3776AB', color: '#3776AB', name: 'Python' },
  { keywords: ['nginx'], icon: 'https://cdn.simpleicons.org/nginx/009639', color: '#009639', name: 'Nginx' },
  { keywords: ['terraform'], icon: 'https://cdn.simpleicons.org/terraform/844FBA', color: '#844FBA', name: 'Terraform' },
  { keywords: ['prometheus'], icon: 'https://cdn.simpleicons.org/prometheus/E6522C', color: '#E6522C', name: 'Prometheus' },
  { keywords: ['grafana'], icon: 'https://cdn.simpleicons.org/grafana/F46800', color: '#F46800', name: 'Grafana' },
  { keywords: ['aws', 'amazon'], icon: 'https://cdn.simpleicons.org/amazonaws/FF9900', color: '#FF9900', name: 'AWS' },
  { keywords: ['azure'], icon: 'https://cdn.simpleicons.org/microsoftazure/0078D4', color: '#0078D4', name: 'Azure' },
  { keywords: ['gcp', 'google cloud'], icon: 'https://cdn.simpleicons.org/googlecloud/4285F4', color: '#4285F4', name: 'GCP' },
  { keywords: ['mysql', 'mariadb'], icon: 'https://cdn.simpleicons.org/mysql/4479A1', color: '#4479A1', name: 'MySQL' },
  { keywords: ['mongodb', 'mongo'], icon: 'https://cdn.simpleicons.org/mongodb/47A248', color: '#47A248', name: 'MongoDB' },
  { keywords: ['redis'], icon: 'https://cdn.simpleicons.org/redis/DC382D', color: '#DC382D', name: 'Redis' },
  { keywords: ['vagrant'], icon: 'https://cdn.simpleicons.org/vagrant/1868F2', color: '#1868F2', name: 'Vagrant' },
  { keywords: ['haproxy'], icon: 'https://cdn.simpleicons.org/haproxy/007DC3', color: '#007DC3', name: 'HAProxy' },
  { keywords: ['nagios'], icon: 'https://cdn.simpleicons.org/nagios/008000', color: '#008000', name: 'Nagios' },
];

function getTechInfo(title) {
  if (!title) return { icon: null, color: '#64748b' };
  const lower = title.toLowerCase();
  for (const entry of TECH_INFO_MAP) {
    if (entry.keywords.some(kw => lower.includes(kw))) {
      return { icon: entry.icon, color: entry.color };
    }
  }
  return { icon: null, color: '#64748b' };
}

/**
 * CourseGrouping - Groups items by their parent course in a tree view with premium brand colors and official technology logos.
 * 
 * Props:
 * - items: array of items with optional course_id / course_title fields
 * - courses: array of courses with id, title, color
 * - renderItem: (item, index, courseColor) => React element
 * - emptyLabel: string label for items without a course (default: "Sans cours")
 * - icon: Lucide icon for uncategorized group header
 */
export function CourseGrouping({ items, courses, renderItem, emptyLabel = "Sans cours", icon: Icon = FolderOpen }) {
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Filter out items whose course is hidden (not present in the courses list)
  const filteredItems = (items || []).filter(item => {
    if (!item.course_id) return true;
    if (!courses || courses.length === 0) return true;
    return courses.some(c => c.id === item.course_id);
  });

  // Build a map of course_id -> course info
  const courseMap = {};
  (courses || []).forEach(c => {
    courseMap[c.id] = c;
  });

  // Group items by course_id
  const groups = {};
  filteredItems.forEach(item => {
    const courseId = (item.course_id && courseMap[item.course_id]) ? item.course_id : "__no_course__";
    if (!groups[courseId]) groups[courseId] = [];
    groups[courseId].push(item);
  });

  // Build sorted group list: courses first (by their order/title), then uncategorized
  const sortedGroupKeys = [];
  // Add courses that have items, in the order they appear in the courses array
  (courses || []).forEach(c => {
    if (groups[c.id]) {
      sortedGroupKeys.push(c.id);
    }
  });
  // Add uncategorized last
  if (groups["__no_course__"]) {
    sortedGroupKeys.push("__no_course__");
  }

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (sortedGroupKeys.length === 0) return null;

  // If there's only one group and it's "__no_course__", skip the tree view
  if (sortedGroupKeys.length === 1 && sortedGroupKeys[0] === "__no_course__") {
    return (
      <div className="grid gap-3">
        {groups["__no_course__"].map((item, idx) => renderItem(item, idx, '#64748b'))}
      </div>
    );
  }

  let globalIndex = 0;

  return (
    <div className="space-y-4">
      {sortedGroupKeys.map(key => {
        const course = courseMap[key];
        const groupItems = groups[key];
        const isCollapsed = collapsedGroups[key];
        const courseName = course ? course.title : emptyLabel;

        // Resolve tech logo icon and color
        const info = course ? getTechInfo(course.title) : { icon: null, color: '#64748b' };
        const courseColor = (course && course.color) ? course.color : info.color;
        const techIcon = info.icon;

        return (
          <div 
            key={key} 
            className="rounded-lg border bg-card overflow-hidden transition-all duration-300"
            style={courseColor ? {
              borderColor: `${courseColor}22`
            } : {}}
          >
            {/* Course Header */}
            <button
              onClick={() => toggleGroup(key)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-300 hover:bg-muted/30",
                !isCollapsed && "border-b"
              )}
              style={courseColor && !isCollapsed ? {
                backgroundColor: `${courseColor}08`,
                borderColor: `${courseColor}22`
              } : {}}
            >
              {isCollapsed ? (
                <ChevronRight 
                  className="h-4 w-4 shrink-0 transition-transform duration-300" 
                  style={courseColor ? { color: courseColor } : {}}
                />
              ) : (
                <ChevronDown 
                  className="h-4 w-4 shrink-0 transition-transform duration-300" 
                  style={courseColor ? { color: courseColor } : {}}
                />
              )}
              
              {techIcon ? (
                <div 
                  className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 border transition-all duration-300" 
                  style={{ 
                    backgroundColor: `${courseColor}12`,
                    borderColor: `${courseColor}33`
                  }}
                >
                  <img src={techIcon} className="w-5 h-5 object-contain" alt="" />
                </div>
              ) : courseColor ? (
                <div 
                  className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 border transition-all duration-300" 
                  style={{ 
                    backgroundColor: `${courseColor}12`,
                    borderColor: `${courseColor}33`
                  }}
                >
                  <BookOpen className="h-4 w-4" style={{ color: courseColor }} />
                </div>
              ) : (
                <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-muted border border-border">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
              )}

              <span 
                className="font-bold text-sm flex-1 uppercase tracking-wide transition-colors"
                style={courseColor ? { color: courseColor } : {}}
              >
                {courseName}
              </span>

              <Badge 
                variant="outline" 
                className="shrink-0 font-bold transition-all"
                style={courseColor ? {
                  borderColor: `${courseColor}33`,
                  color: courseColor,
                  backgroundColor: `${courseColor}12`
                } : {}}
              >
                {groupItems.length}
              </Badge>
            </button>

            {/* Items */}
            {!isCollapsed && (
              <div className="p-3 space-y-3">
                {groupItems.map(item => {
                  const idx = globalIndex++;
                  return (
                    <div key={item.id || idx}>
                      {renderItem(item, idx, courseColor)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
